import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import EmptyState from '../../../components/ui/EmptyState';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPrice = (price) => {
  if (!price) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(price);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })?.format(new Date(d));
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })?.format(new Date(d));
};

// ─── Status Configs ───────────────────────────────────────────────────────────

const SALE_STATUS = {
  DRAFT:          { label: 'Brouillon',        bg: 'bg-gray-50',       text: 'text-gray-500',    border: 'border-gray-200',   icon: 'FileText' },
  LISTED:         { label: 'En vente',         bg: 'bg-blue-50',       text: 'text-blue-600',    border: 'border-blue-100',   icon: 'Home' },
  OFFER_RECEIVED: { label: 'Offre reçue',      bg: 'bg-amber-50',      text: 'text-amber-600',   border: 'border-amber-100',  icon: 'Bell' },
  OFFER_ACCEPTED: { label: 'Offre acceptée',   bg: 'bg-indigo-50',     text: 'text-indigo-600',  border: 'border-indigo-100', icon: 'CheckCircle' },
  FUNDS_HELD:     { label: 'Séquestre actif',  bg: 'bg-brand-gold/10', text: 'text-brand-gold',  border: 'border-brand-gold/20', icon: 'Lock' },
  RELEASED:       { label: 'Fonds libérés',    bg: 'bg-emerald-50',    text: 'text-emerald-600', border: 'border-emerald-100',icon: 'Unlock' },
  COMPLETED:      { label: 'Vente conclue',    bg: 'bg-emerald-50',    text: 'text-emerald-700', border: 'border-emerald-100',icon: 'CheckCircle2' },
  CANCELLED:      { label: 'Annulé',           bg: 'bg-red-50',        text: 'text-red-600',     border: 'border-red-100',    icon: 'XCircle' },
  DISPUTE:        { label: 'Litige',           bg: 'bg-red-50',        text: 'text-red-600',     border: 'border-red-100',    icon: 'AlertTriangle' },
  PENDING:        { label: 'En attente',       bg: 'bg-amber-50',      text: 'text-amber-600',   border: 'border-amber-100',  icon: 'Clock' },
};

const COMMISSION_STATUS = {
  pending:   { label: 'En attente',  bg: 'bg-amber-50',   text: 'text-amber-600',   icon: 'Clock' },
  approved:  { label: 'Approuvée',   bg: 'bg-blue-50',    text: 'text-blue-600',    icon: 'CheckCircle' },
  paid:      { label: 'Versée',      bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'DollarSign' },
  cancelled: { label: 'Annulée',     bg: 'bg-red-50',     text: 'text-red-600',     icon: 'XCircle' },
};

// ─── Sale Progress Steps ──────────────────────────────────────────────────────

const SALE_STEPS = [
  { key: 'LISTED',         label: 'Mise en vente' },
  { key: 'OFFER_ACCEPTED', label: 'Offre acceptée' },
  { key: 'FUNDS_HELD',     label: 'Séquestre' },
  { key: 'RELEASED',       label: 'Fonds libérés' },
  { key: 'COMPLETED',      label: 'Vente conclue' },
];

const STEP_ORDER = { LISTED: 1, OFFER_RECEIVED: 1, OFFER_ACCEPTED: 2, FUNDS_HELD: 3, RELEASED: 4, COMPLETED: 5 };

const SaleProgressBar = ({ status }) => {
  const current = STEP_ORDER?.[status] || 0;
  return (
    <div className="flex items-center gap-1 mt-2">
      {SALE_STEPS?.map((step, idx) => {
        const stepNum = idx + 1;
        const done = current >= stepNum;
        return (
          <React.Fragment key={step?.key}>
            <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold transition-colors ${
                done ? 'bg-brand-earth text-white' : 'bg-border/50 text-text-secondary'
              }`}>
                {done ? <Icon name="Check" size={9} /> : stepNum}
              </div>
              <span className="text-[8px] text-text-secondary whitespace-nowrap hidden sm:block">{step?.label}</span>
            </div>
            {idx < SALE_STEPS?.length - 1 && (
              <div className={`flex-1 h-0.5 rounded-full ${current > stepNum ? 'bg-brand-earth' : 'bg-border/40'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── Document Milestones ──────────────────────────────────────────────────────

const DOC_MILESTONES = [
  { key: 'title_deed',    label: 'Titre foncier',           icon: 'FileText' },
  { key: 'sale_promise',  label: 'Promesse de vente',       icon: 'FileCheck' },
  { key: 'notary_deed',   label: 'Acte notarié',            icon: 'Scale' },
  { key: 'tax_clearance', label: 'Quitus fiscal',           icon: 'Receipt' },
  { key: 'transfer_cert', label: 'Certificat de transfert', icon: 'Award' },
];

const DocumentMilestones = ({ escrow }) => {
  const completed = escrow?.completed_documents || [];
  const total = DOC_MILESTONES?.length;
  const done = DOC_MILESTONES?.filter(d => completed?.includes(d?.key))?.length;

  return (
    <div className="mt-3 pt-3 border-t border-border/30">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">Documents</p>
        <span className="text-[10px] font-bold text-brand-earth">{done}/{total}</span>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {DOC_MILESTONES?.map(doc => {
          const isDone = completed?.includes(doc?.key);
          return (
            <div key={doc?.key} className="flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isDone ? 'bg-emerald-100' : 'bg-border/30'
              }`}>
                <Icon name={doc?.icon} size={14} className={isDone ? 'text-emerald-600' : 'text-text-secondary/50'} />
              </div>
              <span className="text-[8px] text-text-secondary text-center leading-tight hidden sm:block">{doc?.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Payment Timeline ─────────────────────────────────────────────────────────

const PaymentTimeline = ({ escrow }) => {
  const events = [
    escrow?.created_at && { date: escrow?.created_at, label: 'Transaction créée', icon: 'Plus', color: 'text-blue-500' },
    escrow?.status !== 'DRAFT' && escrow?.updated_at && { date: escrow?.updated_at, label: `Statut: ${SALE_STATUS?.[escrow?.status]?.label || escrow?.status}`, icon: 'RefreshCw', color: 'text-amber-500' },
    escrow?.escrow_release_date && { date: escrow?.escrow_release_date, label: 'Fonds libérés', icon: 'Unlock', color: 'text-emerald-500' },
    escrow?.status === 'COMPLETED' && { date: escrow?.updated_at, label: 'Vente conclue', icon: 'CheckCircle2', color: 'text-emerald-600' },
  ]?.filter(Boolean);

  if (!events?.length) return null;

  return (
    <div className="mt-3 pt-3 border-t border-border/30">
      <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Timeline paiement</p>
      <div className="space-y-2">
        {events?.map((ev, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className={`w-5 h-5 rounded-full bg-border/20 flex items-center justify-center flex-shrink-0 mt-0.5`}>
              <Icon name={ev?.icon} size={10} className={ev?.color} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-text-primary">{ev?.label}</p>
              <p className="text-[10px] text-text-secondary">{formatDateTime(ev?.date)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Stats Cards ──────────────────────────────────────────────────────────────

const StatCard = ({ icon, iconBg, iconColor, label, value, sub }) => (
  <div className="bg-white dark:bg-card rounded-xl border border-border/50 p-3.5 flex items-start gap-3">
    <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
      <Icon name={icon} size={16} className={iconColor} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-text-secondary font-medium">{label}</p>
      <p className="text-sm font-bold text-text-primary mt-0.5 truncate">{value}</p>
      {sub && <p className="text-[10px] text-text-secondary mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Section Card ─────────────────────────────────────────────────────────────

const SectionCard = ({ icon, iconBg = 'bg-brand-earth/10', iconColor = 'text-brand-earth', title, subtitle, count, children }) => (
  <div className="bg-white dark:bg-card rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
      <div className="flex items-center space-x-3">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon name={icon} size={18} className={iconColor} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-ios-callout font-body font-semibold text-text-primary">{title}</p>
            {count !== undefined && count > 0 && (
              <span className="px-2 py-0.5 bg-brand-gold/15 text-brand-gold text-[11px] font-bold rounded-full">{count}</span>
            )}
          </div>
          {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

const LoadingSkeleton = ({ rows = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows })?.map((_, i) => (
      <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl bg-surface/60">
        <div className="w-12 h-12 rounded-xl bg-border/50 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 bg-border/50 rounded-lg w-3/4" />
          <div className="h-3 bg-border/40 rounded-lg w-1/2" />
        </div>
        <div className="h-5 w-16 bg-border/40 rounded-lg" />
      </div>
    ))}
  </div>
);

// ─── Main SellerSpaceDashboard ────────────────────────────────────────────────

const SellerSpaceDashboard = ({ userId }) => {
  const [sellerEscrows, setSellerEscrows] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchSellerData = useCallback(async () => {
    if (!userId) return;
    try {
      const [escrowRes, commRes] = await Promise.all([
        supabase
          ?.from('escrow_transactions')
          ?.select(`
            id, title, item_title, amount_total, total_amount, seller_payout_amount, seller_amount,
            kps_commission_amount, platform_fee, currency, status, paystack_reference, reference,
            escrow_release_date, completed_documents, created_at, updated_at,
            property:properties(id, title, address, property_type, commune)
          `)
          ?.eq('seller_id', userId)
          ?.order('created_at', { ascending: false })
          ?.limit(15),
        supabase
          ?.from('commission_payouts')
          ?.select('id, amount, status, commission_type, released_at, created_at, notes, escrow_transaction_id')
          ?.eq('recipient_id', userId)
          ?.order('created_at', { ascending: false })
          ?.limit(10),
      ]);

      setSellerEscrows(escrowRes?.data || []);
      setCommissions(commRes?.data || []);
    } catch (_) {}
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      await fetchSellerData();
      setLoading(false);
    })();

    const channel = supabase
      ?.channel(`seller_space_${userId}`)
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'escrow_transactions', filter: `seller_id=eq.${userId}` }, fetchSellerData)
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'commission_payouts', filter: `recipient_id=eq.${userId}` }, fetchSellerData)
      ?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, [userId, fetchSellerData]);

  const toggleExpand = (id) => setExpanded(prev => prev === id ? null : id);

  // ── Aggregated stats ──
  const totalSaleValue = sellerEscrows?.reduce((s, e) => s + (e?.amount_total || e?.total_amount || 0), 0);
  const totalPayout = sellerEscrows?.reduce((s, e) => s + (e?.seller_payout_amount || e?.seller_amount || 0), 0);
  const escrowHeld = sellerEscrows?.filter(e => e?.status === 'FUNDS_HELD')?.reduce((s, e) => s + (e?.amount_total || e?.total_amount || 0), 0);
  const totalCommission = commissions?.reduce((s, c) => s + (c?.amount || 0), 0);
  const paidCommission = commissions?.filter(c => c?.status === 'paid')?.reduce((s, c) => s + (c?.amount || 0), 0);
  const activeCount = sellerEscrows?.filter(e => !['COMPLETED', 'CANCELLED']?.includes(e?.status))?.length;

  return (
    <div className="space-y-5">
      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon="TrendingUp" iconBg="bg-blue-50" iconColor="text-blue-600"
          label="Valeur totale ventes" value={formatPrice(totalSaleValue)} sub={`${sellerEscrows?.length} transaction(s)`} />
        <StatCard icon="Wallet" iconBg="bg-emerald-50" iconColor="text-emerald-600"
          label="Revenus nets vendeur" value={formatPrice(totalPayout)} sub="Après commissions" />
        <StatCard icon="Lock" iconBg="bg-brand-gold/10" iconColor="text-brand-gold"
          label="Fonds en séquestre" value={formatPrice(escrowHeld)} sub={`${activeCount} actif(s)`} />
        <StatCard icon="DollarSign" iconBg="bg-purple-50" iconColor="text-purple-600"
          label="Commissions agent" value={formatPrice(paidCommission)} sub={`/${formatPrice(totalCommission)} total`} />
      </div>

      {/* ── Sales Tracking ── */}
      <SectionCard
        icon="BarChart3"
        iconBg="bg-blue-50"
        iconColor="text-blue-600"
        title="Suivi des ventes"
        subtitle="Vos transactions en tant que vendeur"
        count={activeCount}
      >
        {loading ? (
          <LoadingSkeleton rows={3} />
        ) : sellerEscrows?.length === 0 ? (
          <EmptyState
            iconName="BarChart3"
            title="Aucune vente en cours"
            description="Vos transactions de vente apparaîtront ici."
            size="sm"
          />
        ) : (
          <div className="space-y-2.5">
            {sellerEscrows?.map(escrow => {
              const cfg = SALE_STATUS?.[escrow?.status] || SALE_STATUS?.PENDING;
              const isOpen = expanded === escrow?.id;
              const title = escrow?.property?.title || escrow?.item_title || escrow?.title || 'Transaction';
              const amount = escrow?.amount_total || escrow?.total_amount || 0;
              const payout = escrow?.seller_payout_amount || escrow?.seller_amount || 0;

              return (
                <div key={escrow?.id} className="rounded-xl border border-border/40 overflow-hidden">
                  <button
                    onClick={() => toggleExpand(escrow?.id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-surface/60 transition-colors text-left"
                  >
                    <div className={`w-10 h-10 rounded-xl ${cfg?.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon name={cfg?.icon} size={16} className={cfg?.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate">{title}</p>
                      <p className="text-xs text-text-secondary mt-0.5">{formatDate(escrow?.created_at)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-sm font-bold text-brand-earth">{formatPrice(amount)}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold border ${cfg?.bg} ${cfg?.text} ${cfg?.border}`}>
                        <Icon name={cfg?.icon} size={9} />{cfg?.label}
                      </span>
                    </div>
                    <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-text-secondary ml-1 flex-shrink-0" />
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 bg-surface/30 border-t border-border/30 space-y-3">
                      <SaleProgressBar status={escrow?.status} />

                      <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                        <div>
                          <p className="text-text-secondary">Montant total</p>
                          <p className="font-bold text-brand-earth">{formatPrice(amount)}</p>
                        </div>
                        <div>
                          <p className="text-text-secondary">Votre part nette</p>
                          <p className="font-bold text-emerald-600">{formatPrice(payout)}</p>
                        </div>
                        {escrow?.kps_commission_amount && (
                          <div>
                            <p className="text-text-secondary">Commission KPS</p>
                            <p className="font-semibold text-text-primary">{formatPrice(escrow?.kps_commission_amount)}</p>
                          </div>
                        )}
                        {escrow?.escrow_release_date && (
                          <div>
                            <p className="text-text-secondary">Date libération</p>
                            <p className="font-semibold text-text-primary">{formatDate(escrow?.escrow_release_date)}</p>
                          </div>
                        )}
                        {escrow?.property?.commune && (
                          <div>
                            <p className="text-text-secondary">Commune</p>
                            <p className="font-semibold text-text-primary">{escrow?.property?.commune}</p>
                          </div>
                        )}
                        {(escrow?.reference || escrow?.paystack_reference) && (
                          <div>
                            <p className="text-text-secondary">Référence</p>
                            <p className="font-mono font-semibold text-text-primary truncate">{escrow?.reference || escrow?.paystack_reference}</p>
                          </div>
                        )}
                      </div>

                      <PaymentTimeline escrow={escrow} />
                      <DocumentMilestones escrow={escrow} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* ── Commission Status ── */}
      <SectionCard
        icon="DollarSign"
        iconBg="bg-purple-50"
        iconColor="text-purple-600"
        title="Commissions agent"
        subtitle="Statut de vos versements de commission"
        count={commissions?.filter(c => c?.status === 'pending')?.length}
      >
        {loading ? (
          <LoadingSkeleton rows={2} />
        ) : commissions?.length === 0 ? (
          <EmptyState
            iconName="DollarSign"
            title="Aucune commission"
            description="Vos commissions apparaîtront ici après la conclusion d'une vente."
            size="sm"
          />
        ) : (
          <div className="space-y-2">
            {commissions?.map(comm => {
              const cfg = COMMISSION_STATUS?.[comm?.status] || COMMISSION_STATUS?.pending;
              return (
                <div key={comm?.id} className="flex items-center gap-3 p-3 rounded-xl bg-surface/50 border border-border/30">
                  <div className={`w-9 h-9 rounded-xl ${cfg?.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={cfg?.icon} size={15} className={cfg?.text} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary capitalize">
                      {comm?.commission_type === 'agent' ? 'Commission agent' : comm?.commission_type === 'notary' ? 'Honoraires notaire' : 'Commission'}
                    </p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      {comm?.released_at ? `Versée le ${formatDate(comm?.released_at)}` : `Créée le ${formatDate(comm?.created_at)}`}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-sm font-bold text-brand-earth">{formatPrice(comm?.amount)}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold ${cfg?.bg} ${cfg?.text}`}>
                      <Icon name={cfg?.icon} size={9} />{cfg?.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default SellerSpaceDashboard;
